import * as math from "mathjs";
import type {
  ExpressionParseContext,
  PartialTargetItem,
  PartialIndexItem,
  PartialResourceItem
} from "../effect/Expression";
import { useSettingsStore } from "@editor/store/settings.store";
import {
  getRoundStartDate,
  roundIndexInSeconds
} from "../simulationRound/useRoundTime";
import { mapValues } from "@/helpers/utility/CollectionFunctions";
import { templateExpressionToExpression } from "./expressionTemplates";

export const functionNames = [
  "randbetween",
  "ifelse",
  "maxValue",
  "minValue",
  "mod",
  "min",
  "max",
  "round",
  "floor",
  "ceil",
  "abs",
  "sign",
  "sqrt",
  "pow",
  "exp",
  "log",
  "delay"
] as const;
export type FunctionName = (typeof functionNames)[number];

export const variableNames = ["x", "maxValue", "minValue", "t"] as const;
export type VariableName = (typeof variableNames)[number];

export const functionsList: {
  [functionName in FunctionName]: {
    args: number;
    connectors?: string[];
    defaultArgs: (number | string)[];
  };
} = {
  abs: {
    args: 1,
    defaultArgs: [0]
  },
  floor: {
    args: 1,
    defaultArgs: [0]
  },
  ceil: {
    args: 1,
    defaultArgs: [0]
  },
  round: {
    args: 1,
    defaultArgs: [0]
  },
  sign: {
    args: 1,
    defaultArgs: [0]
  },
  sqrt: {
    args: 1,
    defaultArgs: [0]
  },
  mod: {
    args: 2,
    connectors: ["mod"],
    defaultArgs: [0, 2]
  },
  randbetween: {
    args: 2,
    connectors: ["and"],
    defaultArgs: [0, 0]
  },
  min: {
    args: 2,
    connectors: ["or"],
    defaultArgs: [0, 0]
  },
  max: {
    args: 2,
    connectors: ["or"],
    defaultArgs: [0, 0]
  },
  maxValue: {
    args: 1,
    defaultArgs: [0]
  },
  minValue: {
    args: 1,
    defaultArgs: [0]
  },
  pow: {
    args: 2,
    connectors: ["pow"],
    defaultArgs: [0, 2]
  },
  exp: {
    args: 1,
    defaultArgs: [0]
  },
  log: {
    args: 1,
    defaultArgs: [0]
  },
  ifelse: {
    args: 3,
    connectors: ["then", "else"],
    defaultArgs: ["(0)", 0, 0]
  },
  delay: {
    args: 2,
    connectors: ["before"],
    defaultArgs: [0, 1]
  }
};

type MathOperator =
  | "+"
  | "-"
  | "*"
  | "/"
  | "<"
  | ">"
  | ">="
  | "<="
  | "=="
  | "!="
  | "^"
  | "&";

/**
 * Returns the mathjs operation tree of an effect expression.
 */
export function getExpressionMathNode(
  expression: string,
  itemContext: Pick<ExpressionParseContext, "targets" | "resources" | "indices">
): math.MathNode {
  let mathExpression = templateExpressionToExpression(expression, itemContext);

  const prefix = mathExpression.charAt(0);

  // remove assignment indicator (can't be parsed automatically, has to be added manually)
  if (prefix == "=") {
    mathExpression = mathExpression.substring(1);
  }
  if (prefix == "*") {
    mathExpression = "1" + mathExpression;
  }

  const mathNode = math.parse(mathExpression);

  if (prefix == "=") {
    // the assignment operator back to the root node
    const wrapper = new math.OperatorNode("==", "equal", [
      new math.ConstantNode(""),
      mathNode
    ]);
    return wrapper;
  }

  return mathNode;
}

export function calculateExpression(
  expression: string,
  context: ExpressionParseContext
): ExpressionValue {
  if (expression.trim() == "") {
    return { value: 0, minValue: 0, maxValue: 0, unit: "", unitValid: true, valid: true };
  }

  const rootMathNode = getExpressionMathNode(expression, context);

  const result = evaluateMathNode(rootMathNode, context);
  if (result == null)
    return { value: 0, minValue: 0, maxValue: 0, unit: "", unitValid: true, valid: false };
  return result;
}


export function calculateExpressionSafe(
  expression: string,
  context: ExpressionParseContext
): ExpressionValue {
  try {
    return calculateExpression(expression, context);
  } catch (error) {
    console.log("error in expression:", expression);
    console.error(error);

    return { value: 0, minValue: 0, maxValue: 0, unit: "", unitValid: true, valid: false };
  }
}

function collectItemValues(
  context: ExpressionParseContext,
  items: {
    [uid: string]: PartialTargetItem | PartialResourceItem | PartialIndexItem;
  }
): {
  [uid: string]: ExpressionValue;
} {
  const values: {
    [uid: string]: ExpressionValue;
  } = {};

  for (const uid in items) {
    const item = items[uid];
    if (item.type === "index") continue;
    values[uid] = {
      value: item.value,
      minValue: item.minValue ?? null,
      maxValue: item.maxValue ?? null,
      unit: item.unit || "",
      unitValid: true,
      valid: true
    };
  }

  // overwrite the default values with the values from the active round
  const rounds = context.rounds || [];
  if (rounds.length > 0) {
    // add a virtual start round to the beginning of the rounds array
    rounds[-1] = {
      index: -1,
      elementValues: {
        ...mapValues(context.targets || {}, (e) => ({
          value: e.value,
          minValue: e.minValue ?? null,
          maxValue: e.maxValue ?? null,
          unit: e.unit ?? "",
          unitValid: true,
          valid: true
        })),
        ...mapValues(context.resources || {}, (e) => ({
          value: e.value,
          minValue: e.minValue ?? null,
          maxValue: e.maxValue ?? null,
          unit: e.unit ?? "",
          unitValid: true,
          valid: true
        }))
      }
    };
  }

  // get the active round from the context
  let roundIndex = context?.roundIndex ?? context?.index;
  let activeRound = null;
  if (roundIndex != null) {
    roundIndex = Math.max(-1, roundIndex);
    for (let i in rounds) {
      if (rounds[i].index == roundIndex) {
        activeRound = rounds[i];
        break;
      }
    }
  } else {
    roundIndex = 0;
  }
  if (activeRound == null) {
    activeRound = rounds[rounds.length - 1] || {};
  }

  const roundElementValues = activeRound.elementValues;
  if (roundElementValues != null) {
    for (const uid in roundElementValues) {
      const saved = roundElementValues[uid];
      if (!saved) continue;

      if (values[uid] == null) {
        values[uid] = { ...saved };
      } else {
        values[uid].value = saved.value;
      }
    }
  }

  for (const uid in context.indexValueCache) {
    const cachedValue = context.indexValueCache[uid];
    values[uid] = { ...cachedValue };
  }

  // if (context.affectedType != null && context.affectedId != null) {
  //   const uid = context.affectedType + "_" + context.affectedId;
  //   const item = items[uid];
  //   if (item && item.type != "index") {
  //     values["x"] = values[uid] || 0;
  //   }
  // }

  const settingsStore = useSettingsStore();
  const simSettings = context.simulationSettings || {};
  const maxRoundIndex =
    (simSettings.roundAmount ?? settingsStore.simulationRoundAmount) - 1;
  const roundUnit = simSettings.roundUnit ?? settingsStore.simulationRoundUnit;
  const roundLength =
    simSettings.roundLength ?? settingsStore.simulationRoundLength;
  const durationType =
    simSettings.durationType ?? settingsStore.simulationDurationType;
  const timeStart =
    durationType == "roundAmount"
      ? null
      : simSettings.timeStart ?? settingsStore.simulationTimeStart;

  // global variables
  values["t"] = {
    value: roundIndex + 1,
    unit: "",
    unitValid: true,
    minValue: 1,
    maxValue: maxRoundIndex + 1,
    valid: true
  };

  const targetUids = Object.values(context.targets || {}).map((e) => e.uid);
  const resourceUids = Object.values(context.resources || {}).map((e) => e.uid);
  const indexUids = Object.values(context.indices || {}).map((e) => e.uid);

  values["targetCount"] = {
    value: targetUids.length,
    unit: "",
    unitValid: true,
    minValue: targetUids.length,
    maxValue: targetUids.length,
    valid: true
  };
  values["resourceCount"] = {
    value: resourceUids.length,
    unit: "",
    unitValid: true,
    minValue: resourceUids.length,
    maxValue: resourceUids.length,
    valid: true
  };
  values["indexCount"] = {
    value: indexUids.length,
    unit: "",
    unitValid: true,
    minValue: indexUids.length,
    maxValue: indexUids.length,
    valid: true
  };

  values["PI"] = {
    value: Math.PI,
    unit: "",
    unitValid: true,
    minValue: Math.PI,
    maxValue: Math.PI,
    valid: true
  };
  values["E"] = {
    value: Math.E,
    unit: "",
    unitValid: true,
    minValue: Math.E,
    maxValue: Math.E,
    valid: true
  };

  if (roundUnit == "round") {
    values["year"] = {
      ...values["t"]
    };
    values["month"] = {
      ...values["t"]
    };
    values["week"] = {
      ...values["t"]
    };
    values["day"] = {
      ...values["t"]
    };
    values["hour"] = {
      ...values["t"]
    };
    values["minute"] = {
      ...values["t"]
    };
    values["second"] = {
      ...values["t"]
    };
  } else if (timeStart == null) {
    const seconds = roundIndexInSeconds(roundUnit, roundLength, roundIndex);
    const maxSeconds = roundIndexInSeconds(
      roundUnit,
      roundLength,
      maxRoundIndex
    );
    values["year"] = {
      value: seconds / (365 * 24 * 60 * 60),
      unit: "",
      unitValid: true,
      minValue: 0,
      maxValue: maxSeconds / (365 * 24 * 60 * 60),
      valid: true
    };
    values["month"] = {
      value: seconds / (30 * 24 * 60 * 60),
      unit: "",
      unitValid: true,
      minValue: 0,
      maxValue: maxSeconds / (30 * 24 * 60 * 60),
      valid: true
    };
    values["week"] = {
      value: seconds / (7 * 24 * 60 * 60),
      unit: "",
      unitValid: true,
      minValue: 0,
      maxValue: maxSeconds / (7 * 24 * 60 * 60),
      valid: true
    };
    values["day"] = {
      value: seconds / (24 * 60 * 60),
      unit: "",
      unitValid: true,
      minValue: 0,
      maxValue: maxSeconds / (24 * 60 * 60),
      valid: true
    };
    values["hour"] = {
      value: seconds / (60 * 60),
      unit: "",
      unitValid: true,
      minValue: 0,
      maxValue: maxSeconds / (60 * 60),
      valid: true
    };
    values["minute"] = {
      value: seconds / 60,
      unit: "",
      unitValid: true,
      minValue: 0,
      maxValue: maxSeconds / 60,
      valid: true
    };
    values["second"] = {
      value: seconds,
      unit: "",
      unitValid: true,
      minValue: 0,
      maxValue: maxSeconds,
      valid: true
    };
  } else {
    const currentDateTime = getRoundStartDate(
      roundUnit,
      roundLength,
      roundIndex,
      timeStart
    );
    const minDateTime = getRoundStartDate(roundUnit, roundLength, 0, timeStart);
    const maxDateTime = getRoundStartDate(
      roundUnit,
      roundLength,
      maxRoundIndex,
      timeStart
    );

    values["year"] = {
      value: currentDateTime.year,
      unit: "",
      unitValid: true,
      minValue: minDateTime.year,
      maxValue: maxDateTime.year,
      valid: true
    };
    values["month"] = {
      value: currentDateTime.month,
      unit: "",
      unitValid: true,
      minValue: Math.min(minDateTime.month, maxDateTime.month),
      maxValue: Math.max(minDateTime.month, maxDateTime.month),
      valid: true
    };
    values["week"] = {
      value: currentDateTime.weekNumber,
      unit: "",
      unitValid: true,
      minValue: Math.min(minDateTime.weekNumber, maxDateTime.weekNumber),
      maxValue: Math.max(minDateTime.weekNumber, maxDateTime.weekNumber),
      valid: true
    };
    values["day"] = {
      value: currentDateTime.day,
      unit: "",
      unitValid: true,
      minValue: Math.min(minDateTime.day, maxDateTime.day),
      maxValue: Math.max(minDateTime.day, maxDateTime.day),
      valid: true
    };
    values["hour"] = {
      value: currentDateTime.hour,
      unit: "",
      unitValid: true,
      minValue: Math.min(minDateTime.hour, maxDateTime.hour),
      maxValue: Math.max(minDateTime.hour, maxDateTime.hour),
      valid: true
    };
    values["minute"] = {
      value: currentDateTime.minute,
      unit: "",
      unitValid: true,
      minValue: Math.min(minDateTime.minute, maxDateTime.minute),
      maxValue: Math.max(minDateTime.minute, maxDateTime.minute),
      valid: true
    };
    values["second"] = {
      value: currentDateTime.second,
      unit: "",
      unitValid: true,
      minValue: Math.min(minDateTime.second, maxDateTime.second),
      maxValue: Math.max(minDateTime.second, maxDateTime.second),
      valid: true
    };
  }

  return values;
}

function collectItems(context: ExpressionParseContext): {
  [uid: string]: PartialTargetItem | PartialResourceItem | PartialIndexItem;
} {
  const items: {
    [uid: string]: PartialTargetItem | PartialResourceItem | PartialIndexItem;
  } = {};

  if (context.targets != null) {
    for (const targetId in context.targets) {
      const target = context.targets[targetId];
      items[target.uid] = target;
    }
  }
  if (context.resources != null) {
    for (const resourceId in context.resources) {
      const resource = context.resources[resourceId];
      items[resource.uid] = resource;
    }
  }
  if (context.indices != null) {
    for (const indexId in context.indices) {
      const index = context.indices[indexId];
      items[index.uid] = index;
    }
  }

  return items;
}

type ExpressionFunctions = {
  [key in MathOperator | FunctionName]: (
    ...args: (ExpressionValue<number | boolean> | null)[]
  ) => number | boolean;
};

export type ExpressionValue<
  V extends number | boolean | string | null = number | boolean
> = {
  value: V;
  minValue: number | null;
  maxValue: number | null;
  unit: string;
  unitValid: boolean;
  valid?: boolean; // is not set in old simulations
};

export function evaluateMathNode(
  rootNode: math.MathNode,
  context: ExpressionParseContext,
  items?: {
    [uid: string]: PartialTargetItem | PartialResourceItem | PartialIndexItem;
  },
  itemValues?: {
    [uid: string]: ExpressionValue;
  }
): ExpressionValue & { constantValue?: number } {
  if (!items) items = collectItems(context);
  if (!itemValues) {
    itemValues = collectItemValues(context, items);
  }

  const functionValueGetters: ExpressionFunctions = {
    "+": (a, b) => {
      if (a == null) return b == null ? 0 : Number(b.value);
      if (b == null) return Number(a.value);
      return Number(a.value) + Number(b.value);
    },
    "-": (a, b) => {
      if (a == null) return b == null ? 0 : -Number(b.value);
      if (b == null) return -Number(a.value);
      return Number(a.value) - Number(b.value);
    },
    "*": (a, b) => {
      if (a == null || b == null) return 0;
      return Number(a.value) * Number(b.value);
    },
    "/": (a, b) => {
      if (a == null || b == null) return 0;
      return Number(a.value) / Number(b.value);
    },
    "<": (a, b) => {
      if (a == null || b == null) return false;
      return Number(a.value) < Number(b.value);
    },
    ">": (a, b) => {
      if (a == null || b == null) return false;
      return Number(a.value) > Number(b.value);
    },
    ">=": (a, b) => {
      if (a == null || b == null) return false;
      return Number(a.value) >= Number(b.value);
    },
    "<=": (a, b) => {
      if (a == null || b == null) return false;
      return Number(a.value) <= Number(b.value);
    },
    "==": (a, b) => {
      if (b == null) return false;
      if (!a) return b.value;
      return Number(a.value) == Number(b.value);
    },
    "!=": (a, b) => {
      if (a == null || b == null) return false;
      return Number(a.value) != Number(b.value);
    },
    "&": (a, b) => {
      if (a == null || b == null) return false;
      return Number(a.value) && Number(b.value);
    },
    "^": (a, b) => {
      if (a == null || b == null) return 0;
      return Math.pow(Number(a.value), Number(b.value));
    },
    randbetween: (a, b) => {
      if (a == null || b == null) return 0;
      const min = Math.min(Number(a.value), Number(b.value));
      const max = Math.max(Number(a.value), Number(b.value));
      const rand = context.fixedRandom ?? Math.random();
      return Math.floor(rand * (max - min + 1)) + min;
    },
    ifelse: (a, b, c) => {
      if (a == null || b == null || c == null) return 0;
      return a.value ? Number(b.value) : Number(c.value);
    },
    mod: (a, b) => {
      if (a == null || b == null) return 0;
      return Number(a.value) % Number(b.value);
    },
    min: (a, b) => {
      if (a == null || b == null) return 0;
      return Math.min(Number(a.value), Number(b.value));
    },
    max: (a, b) => {
      if (a == null || b == null) return 0;
      return Math.max(Number(a.value), Number(b.value));
    },
    round: (a) => {
      if (a == null) return 0;
      return Math.round(Number(a.value));
    },
    floor: (a) => {
      if (a == null) return 0;
      return Math.floor(Number(a.value));
    },
    ceil: (a) => {
      if (a == null) return 0;
      return Math.ceil(Number(a.value));
    },
    abs: (a) => {
      if (a == null) return 0;
      return Math.abs(Number(a.value));
    },
    sign: (a) => {
      if (a == null) return 0;
      return Math.sign(Number(a.value));
    },
    sqrt: (a) => {
      if (a == null) return 0;
      return Math.sqrt(Number(a.value));
    },
    pow: (a, b) => {
      if (a == null || b == null) return 0;
      return Math.pow(Number(a.value), Number(b.value));
    },
    exp: (a) => {
      if (a == null) return 0;
      return Math.exp(Number(a.value));
    },
    log: (a) => {
      if (a == null) return 0;
      return Math.log(Number(a.value));
    },
    maxValue: (a) => {
      if (a == null) return 0;
      return a.maxValue ?? 0;
    },
    minValue: (a) => {
      if (a == null) return 0;
      return a.minValue ?? 0;
    },
    delay: (a, b) => {
      if (a == null || b == null) return 0;
      return Number(a.value);
    }
  };
  
  const randomValuesCache = context.randomValues || {};
  let randomKey = context.randomKey || (context.affectedType ?? "") + "_" + (context.affectedId ?? "");

  if (rootNode instanceof math.SymbolNode) {
    const uid = rootNode.name;
    const value = itemValues[uid];
    const { type, id } = separateUid(uid);

    if (
      type === "index" &&
      id === context.affectedId &&
      context.affectedType === "index"
    ) {
      throw new Error(`Infinite recursive loop for ${uid}`);
    }

    if (value == null) {
      if (type === "index") {
        // try to get the index value from the cache
        if (context.indexValueCache != null) {
          const indexValue = context.indexValueCache[`index_${id}`];
          if (indexValue != null) {
            return indexValue;
          }
        }

        // No value found in the cache.

        // Try to calculate the index value.
        const index = context.indices?.[id];
        if (index == null) {
          console.warn("index not found", uid, context.indices);
          throw new Error(`Index ${uid} not found`);
        }

        // prevent infinite loops
        if (context.callstack?.includes?.(uid)) {
          console.warn("infinite loop", uid, context.callstack);
          throw new Error(`Infinite recursive loop for ${uid}`);
        }

        // calculate the index value
        const indexValue = calculateExpression(index.valueExpression, {
          ...context,
          indexValueCache: context.indexValueCache || {},
          callstack: [...(context.callstack || []), uid],
          randomValues: context.randomValues || {},
          randomKey: uid
        });

        if (index.unit) {
          indexValue.unit = index.unit;
          indexValue.unitValid = true;
        }

        // save the index value in the cache
        if (context.indexValueCache) {
          context.indexValueCache[`index_${id}`] = indexValue;
        }

        return indexValue;
      }
      throw new Error(`Value for ${rootNode.name} not found`);
    }
    return value;
  }

  if (rootNode instanceof math.ConstantNode) {
    const value = Number(rootNode.value || 0);
    return {
      value: value,
      minValue: value,
      maxValue: value,
      unit: "",
      unitValid: true,
      valid: true,
      constantValue: value
    };
  }

  if (rootNode instanceof math.FunctionNode) {
    const functionName: FunctionName = rootNode.fn.name;
    const getFunctionValue = functionValueGetters[functionName];
    if (getFunctionValue == null) {
      throw new Error(`Function ${functionName} not found`);
    }

    let argNodes = rootNode.args;

    const requiredArgsCount = functionsList[functionName].args;

    if (argNodes.length < requiredArgsCount) {
      throw new ReferenceError(
        "Not enough parameters for " +
          functionName +
          "(" +
          argNodes.length +
          "<" +
          requiredArgsCount +
          ")"
      );
    }

    let args: (ExpressionValue | null)[] = [];

    if (functionName === "delay") {
      const delayResult = evaluateMathNode(
        argNodes[1],
        {
          ...context,
          randomKey: randomKey + ".1"
        },
        items,
        itemValues
      );
      const delay = Number(delayResult.value);
      const newContext = {
        ...context,
        roundIndex:
          (context.roundIndex ?? (context.rounds?.length ?? 1) - 1) - delay,
        indexValueCache: {},
        randomValues: {},
        randomKey: randomKey + ".0"
      };
      const result = evaluateMathNode(argNodes[0], newContext, items);
      args = [result, delayResult];
    } else {
      args = (rootNode.args as []).map((arg: math.MathNode, index: number) =>
        evaluatateMathArgumentNode(arg, {...context, randomKey: randomKey + `.${index}` }, items, itemValues)
      );
    }

    const { minValue, maxValue } = expressionFunctionRanges[functionName](
      ...args
    );
    const { unit, unitValid } = expressionFunctionUnits[functionName](...args);

    if (functionName === "randbetween" && context.fixedRandom == null) {
      const randomValue = randomValuesCache[randomKey];
      if (randomValue != null) {
        return {
          value: randomValue,
          minValue: minValue,
          maxValue: maxValue,
          unit: unit,
          unitValid: unitValid,
          valid: true
        };
      }
    }

    const value = getFunctionValue(...args);

    if (functionName === "randbetween" && context.fixedRandom == null) {
      randomValuesCache[randomKey] = value;
    }

    return {
      value,
      minValue,
      maxValue,
      unit,
      unitValid,
      valid: true
    };
  }

  if (rootNode instanceof math.OperatorNode) {
    const operatorName: MathOperator = rootNode.op;
    const args = rootNode.args.map((arg: math.MathNode, index: number) =>
      evaluatateMathArgumentNode(arg, {...context, randomKey: randomKey + `.${index}` }, items, itemValues)
    );
    const getFunctionValue = functionValueGetters[operatorName];
    if (getFunctionValue == null) {
      console.warn(`${operatorName} not found`, rootNode);
      throw new Error(`Operator ${operatorName} not found`);
    }

    const { minValue, maxValue } = expressionFunctionRanges[operatorName](
      ...args
    );
    const { unit, unitValid } = expressionFunctionUnits[operatorName](...args);
    const value = getFunctionValue(...args);
    return {
      value,
      minValue,
      maxValue,
      unit,
      unitValid,
      valid: true
    };
  }

  if (rootNode instanceof math.ParenthesisNode) {
    const result = evaluateMathNode(
      rootNode.content,
      context,
      items,
      itemValues
    );
    return result;
  }

  console.warn("can't calculate", rootNode);
  throw new Error(`Can't calculate node type ${rootNode.type}`);
}

function evaluatateMathArgumentNode(
  arg: math.MathNode,
  context: ExpressionParseContext,
  items?: {
    [uid: string]: PartialTargetItem | PartialResourceItem | PartialIndexItem;
  },
  itemValues?: {
    [uid: string]: ExpressionValue;
  }
) {
  if (arg instanceof math.ConstantNode) {
    if (typeof arg.value == "string") return null;
  }
  return evaluateMathNode(arg, context, items, itemValues);
}

// ============================
//      RANGES (min/max)
// ============================

type MinMaxExpressionValue = Pick<ExpressionValue, "minValue" | "maxValue">;

/**
 * Collection of functions that calculate the minimum and maximum value of an effect mathjs operation.
 */
type ExpressionFunctionRanges = {
  [key in MathOperator | FunctionName]: (
    ...args: (MinMaxExpressionValue | null)[]
  ) => MinMaxExpressionValue;
};

const expressionFunctionRanges: ExpressionFunctionRanges = {
  "+": (a, b) => {
    if (a == null && b == null) return { minValue: null, maxValue: null };
    if (b == null) return a!;
    if (a == null) return b!;
    return {
      minValue: add(a?.minValue, b?.minValue),
      maxValue: add(a?.maxValue, b?.maxValue)
    };
  },
  "-": (a, b) => {
    if (a == null && b == null) return { minValue: null, maxValue: null };
    if (b == null) {
      return {
        minValue: substract(0, a!.maxValue),
        maxValue: substract(0, a!.minValue)
      };
    }
    if (a == null) {
      return {
        minValue: substract(0, b!.maxValue),
        maxValue: substract(0, b!.minValue)
      };
    }
    return {
      minValue: substract(a.minValue, b.maxValue),
      maxValue: substract(a.maxValue, b.minValue)
    };
  },
  "*": (a, b) => {
    if (a == null) return { minValue: null, maxValue: null };

    let aMaxValue = a.maxValue ?? Infinity;
    let aMinValue = a.minValue ?? -Infinity;
    let bMaxValue = b?.maxValue ?? Infinity;
    let bMinValue = b?.minValue ?? -Infinity;

    const values = [
      aMinValue * bMinValue,
      aMinValue * bMaxValue,
      aMaxValue * bMinValue,
      aMaxValue * bMaxValue
    ].filter((v) => v != null && !isNaN(v));

    return {
      minValue: Math.min(...values),
      maxValue: Math.max(...values)
    };
  },
  "/": (a, b) => {
    if (a == null) return { minValue: null, maxValue: null };

    if (!hasMinAndMax(a) || !hasMinAndMax(b))
      return { minValue: null, maxValue: null };

    const aMin = a.minValue;
    const aMax = a.maxValue;
    const bMin = b.minValue;
    const bMax = b.maxValue;

    if (aMin == null || aMax == null || bMin == null || bMax == null) {
      return { minValue: null, maxValue: null };
    }
    // prevent division by zero
    // The range goes up to infinity, but we can't represent that.
    const values = [aMin / bMin, aMin / bMax, aMax / bMin, aMax / bMax].filter(
      (v) => v != null && !isNaN(v)
    );

    const minMax = {
      minValue: Math.min(...values) ?? null,
      maxValue: Math.max(...values) ?? null
    };
    return minMax;
  },
  "<": (_a, _b) => ({ minValue: 0, maxValue: 1 }),
  ">": (_a, _b) => ({ minValue: 0, maxValue: 1 }),
  ">=": (_a, _b) => ({ minValue: 0, maxValue: 1 }),
  "<=": (_a, _b) => ({ minValue: 0, maxValue: 1 }),
  "==": (a, b) => {
    if (!a) return b ?? { minValue: 0, maxValue: 1 };
    return { minValue: 0, maxValue: 1 };
  },
  "!=": (_a, _b) => ({ minValue: 0, maxValue: 1 }),
  "^": (a, b) => {
    if (!hasMinAndMax(a) || !hasMinAndMax(b))
      return { minValue: null, maxValue: null };
    const lower = Math.pow(Number(a.minValue), Number(b.minValue));
    const higher = Math.pow(Number(a.maxValue), Number(b.maxValue));
    return {
      minValue: Math.min(lower, higher),
      maxValue: Math.max(lower, higher)
    };
  },
  "&": (_a, _b) => ({ minValue: 0, maxValue: 1 }),
  randbetween: (a, b) => {
    if (!a) return { minValue: null, maxValue: null };
    if (!b) return { minValue: null, maxValue: null };
    if (!hasMinAndMax(a) || !hasMinAndMax(b))
      return { minValue: null, maxValue: null };
    return {
      minValue: Math.min(a.minValue, b.minValue),
      maxValue: Math.max(a.maxValue, b.maxValue)
    };
  },
  ifelse: (a, b, c) => {
    if (!a) return { minValue: null, maxValue: null };

    if (!hasMinAndMax(b) || !hasMinAndMax(c))
      return { minValue: null, maxValue: null };
    return {
      minValue: Math.min(b.minValue, c.minValue),
      maxValue: Math.max(b.maxValue, c.maxValue)
    };
  },
  mod: (a, b) => {
    if (!hasMinAndMax(a) || !hasMinAndMax(b))
      return { minValue: null, maxValue: null };
    if (!a) return { minValue: 0, maxValue: 0 };
    return { minValue: 0, maxValue: Math.max(a.maxValue, b.maxValue) };
  },
  min: (a, b) => {
    if (!hasMinAndMax(a) || !hasMinAndMax(b))
      return { minValue: null, maxValue: null };
    return {
      minValue: Math.min(a.minValue, b.minValue),
      maxValue: Math.min(a.maxValue, b.maxValue)
    };
  },
  max: (a, b) => {
    if (!hasMinAndMax(a) || !hasMinAndMax(b))
      return { minValue: null, maxValue: null };
    return {
      minValue: Math.max(a.minValue, b.minValue),
      maxValue: Math.max(a.maxValue, b.maxValue)
    };
  },
  round: (a) => {
    if (!hasMinAndMax(a)) return { minValue: null, maxValue: null };
    return {
      minValue: Math.round(a.minValue),
      maxValue: Math.round(a.maxValue)
    };
  },
  floor: (a) => {
    if (!hasMinAndMax(a)) return { minValue: null, maxValue: null };
    return {
      minValue: Math.floor(a.minValue),
      maxValue: Math.floor(a.maxValue)
    };
  },
  ceil: (a) => {
    if (!hasMinAndMax(a)) return { minValue: null, maxValue: null };
    return { minValue: Math.ceil(a.minValue), maxValue: Math.ceil(a.maxValue) };
  },
  abs: (a) => {
    if (!hasMinAndMax(a)) return { minValue: null, maxValue: null };
    return { minValue: Math.abs(a.minValue), maxValue: Math.abs(a.maxValue) };
  },
  sign: (a) => {
    if (!hasMinAndMax(a)) return { minValue: null, maxValue: null };
    return { minValue: Math.sign(a.minValue), maxValue: Math.sign(a.maxValue) };
  },
  sqrt: (a) => {
    if (!hasMinAndMax(a)) return { minValue: null, maxValue: null };
    return { minValue: Math.sqrt(a.minValue), maxValue: Math.sqrt(a.maxValue) };
  },
  pow: (a, b) => {
    if (!hasMinAndMax(a) || !hasMinAndMax(b))
      return { minValue: null, maxValue: null };
    return {
      minValue: Math.pow(a.minValue, b.minValue),
      maxValue: Math.pow(a.maxValue, b.maxValue)
    };
  },
  exp: (a) => {
    if (!hasMinAndMax(a)) return { minValue: null, maxValue: null };
    return {
      minValue: Math.exp(a.minValue),
      maxValue: Math.exp(a.maxValue)
    };
  },
  log: (a) => {
    if (!hasMinAndMax(a)) return { minValue: null, maxValue: null };
    return {
      minValue: Math.log(a.minValue),
      maxValue: Math.log(a.maxValue)
    };
  },
  maxValue: (a) => {
    if (!a) return { minValue: null, maxValue: null };
    return { minValue: a.maxValue, maxValue: a.maxValue };
  },
  minValue: (a) => {
    if (!a) return { minValue: null, maxValue: null };
    return { minValue: a.minValue, maxValue: a.minValue };
  },
  delay: (a, b) => {
    if (!a) return { minValue: null, maxValue: null };
    return {
      minValue: a.minValue,
      maxValue: a.maxValue
    };
  }
};
function add(
  a: number | null | undefined,
  b: number | null | undefined
): number | null {
  if (a == null || b == null) return null;
  return a + b;
}
function substract(
  a: number | null | undefined,
  b: number | null | undefined
): number | null {
  if (a == null || b == null) return null;
  return a - b;
}

/**
 * Makes sure that the min and max values are not null.
 */
function hasMinAndMax(
  a?: MinMaxExpressionValue | null
): a is { minValue: number; maxValue: number } {
  return a?.minValue != null && a?.maxValue != null;
}

/**
 * Collection of functions that calculate the minimum and maximum value of an effect mathjs operation.
 */
type ExpressionFunctionUnits = {
  [key in MathOperator | FunctionName]: (
    ...args: (Pick<ExpressionValue, "unit" | "unitValid"> & { constantValue?: number; maxValue?: number | boolean | null } | null)[]
  ) => { unit: string; unitValid: boolean };
};
const expressionFunctionUnits: ExpressionFunctionUnits = {
  "+": (a, b) => {
    if (!a) return { unit: "", unitValid: true };

    if (a.unit === b?.unit) return a;
    if (!a.unit) return b || { unit: "", unitValid: true };
    if (!b?.unit) return a;
    return { unit: `${a.unit}+${b.unit}`, unitValid: false };
  },
  "-": (a, b) => {
    if (!a) return { unit: "", unitValid: true };

    if (a.unit === b?.unit) return a;
    if (!a.unit) return b || { unit: "", unitValid: true };
    if (!b?.unit) return a;
    return { unit: `${a.unit}-${b.unit}`, unitValid: false };
  },
  "*": (a, b) => {
    if (!a || !b) return { unit: "", unitValid: true };

    if (a.unit === b.unit) return a;
    if (!a.unit) return b;
    if (!b.unit) return a;
    const [aLeft = "", aRight = ""] = a.unit.split(/[\s]*\/[\s]*/);
    const [bLeft = "", bRight = ""] = b.unit.split(/[\s]*\/[\s]*/); // reverse order, because of division
    const left = [...aLeft.split(/​/), ...bLeft.split(/​/)].filter(
      (x) => x != "1" && x != ""
    );
    const right = [...aRight.split(/​/), ...bRight.split(/​/)].filter(
      (x) => x != "1" && x != ""
    );

    // remove units that appear in both numerator and denominator
    for (let i = 0; i < left.length; i++) {
      const unit = left[i];
      const index = right.indexOf(unit);
      if (index != -1) {
        right.splice(index, 1);
        left.splice(i, 1);
        i--;
      }
    }

    if (right.length == 0) {
      return {
        unit: left.join("​"),
        unitValid: true
      };
    }
    if (left.length == 0) left.push("1");
    const combined = [left.join("​"), right.join("​")];
    return {
      unit: combined.join("/"),
      unitValid: true
    };
  },
  "/": (a, b) => {
    if (!a || !b) return { unit: "", unitValid: true };

    if (a.unit === b.unit) {
      // 6 Apples / 2 Apples = 3  (without unit)
      return {
        unit: "",
        unitValid: true
      };
    }

    if (!a.unit) {
      return {
        unit: "1/" + b.unit,
        unitValid: true
      };
    }

    if (!b.unit) {
      if (b.constantValue == null) return a;
    }

    const [aLeft = "", aRight = ""] = a.unit.split(/[\s]*\/[\s]*/);
    const [bRight = "", bLeft = ""] = b.unit.split(/[\s]*\/[\s]*/); // reverse order, because of division

    const left = [...aLeft.split(/​/), ...bLeft.split(/​/)].filter(
      (x) => x != "1" && x != ""
    );
    const right = [...aRight.split(/​/), ...bRight.split(/​/)].filter(
      (x) => x != "1" && x != ""
    );

    // Add special case for percentages:
    // 50% / 100 = 0.5 (without unit)
    if (left.includes("%") && a.maxValue === 100 && right.length === 0 && b.constantValue === 100) {
      const index = left.indexOf("%");
      left.splice(index, 1);
    }

    // remove units that appear in both numerator and denominator
    for (let i = 0; i < left.length; i++) {
      const unit = left[i];
      const index = right.indexOf(unit);
      if (index != -1) {
        right.splice(index, 1);
        left.splice(i, 1);
        i--;
      }
    }

    if (right.length == 0) {
      return {
        unit: left.join("​"),
        unitValid: true
      };
    }
    if (left.length == 0) left.push("1");
    const combined = [left.join("​"), right.join("​")];
    return {
      unit: combined.join("/"),
      unitValid: true
    };
  },
  "^": (a, _b) => a || { unit: "", unitValid: true },
  "<": (_a, _b) => ({ unit: "", unitValid: true }),
  ">": (_a, _b) => ({ unit: "", unitValid: true }),
  ">=": (_a, _b) => ({ unit: "", unitValid: true }),
  "<=": (_a, _b) => ({ unit: "", unitValid: true }),
  "==": (_a, _b) => ({ unit: "", unitValid: true }),
  "!=": (_a, _b) => ({ unit: "", unitValid: true }),
  "&": (_a, _b) => ({ unit: "", unitValid: true }),
  randbetween: (a, b) => {
    if (!a || !b) return { unit: "", unitValid: true };

    if (a.unit === b.unit) return a;
    if (!a.unit) return b;
    if (!b.unit) return a;
    return {
      unit: `${a.unit}|${b.unit}`,
      unitValid: false
    };
  },
  ifelse: (a: unknown, b, c) => {
    if (!a || !b || !c) return { unit: "", unitValid: true };

    if (b.unit === c.unit) return b;
    if (!b.unit) return c;
    if (!c.unit) return b;
    return {
      unit: `${b.unit}|${c.unit}`,
      unitValid: false
    };
  },
  mod: (a, b) => {
    if (!a || !b) return { unit: "", unitValid: true };

    if (a.unit === b.unit) return a;
    if (!a.unit) return b;
    if (!b.unit) return a;
    return { unit: `${a.unit}%${b.unit}`, unitValid: false };
  },
  min: (a, b) => {
    if (!a || !b) return { unit: "", unitValid: true };

    if (a.unit === b.unit) return a;
    if (!a.unit) return b;
    if (!b.unit) return a;
    return { unit: `${a.unit}|${b.unit}`, unitValid: false };
  },
  max: (a, b) => {
    if (!a || !b) return { unit: "", unitValid: true };

    if (a.unit === b.unit) return a;
    if (!a.unit) return b;
    if (!b.unit) return a;
    return {
      unit: `${a.unit}|${b.unit}`,
      unitValid: false
    };
  },
  round: (a) => a || { unit: "", unitValid: true },
  floor: (a) => a || { unit: "", unitValid: true },
  ceil: (a) => a || { unit: "", unitValid: true },
  abs: (a) => a || { unit: "", unitValid: true },
  sign: (a) => a || { unit: "", unitValid: true },
  sqrt: (a) => a || { unit: "", unitValid: true },
  pow: (a, b) => {
    if (!a || !b) return { unit: "", unitValid: true };

    if (a.unit === b.unit) return a;
    if (!a.unit) return b;
    if (!b.unit) return a;
    return {
      unit: `${a.unit}^${b.unit}`,
      unitValid: false
    };
  },
  exp: (a) => a || { unit: "", unitValid: true },
  log: (a) => a || { unit: "", unitValid: true },
  maxValue: (a) => a || { unit: "", unitValid: true },
  minValue: (a) => a || { unit: "", unitValid: true },
  delay: (a, b) => {
    if (!a) return { unit: "", unitValid: true };

    return {
      unit: a.unit,
      unitValid: a.unitValid
    };
  }
};

function separateUid(uid: string) {
  const [type, id] = uid.split("_");
  return { type: type as "target" | "index" | "resource", id: parseInt(id) };
}

export function isExpressionRandom(expression: string, context: ExpressionParseContext) {
  const tree = getExpressionMathNode(expression, context);
  return isMathTreeRandom(tree, context);
}

export function isMathTreeRandom(
  tree: math.MathNode,
  context: ExpressionParseContext
): boolean {
  if (tree instanceof math.FunctionNode) {
    const functionName: FunctionName = tree.fn.name;
    if (functionName === "randbetween") {
      return true;
    }
  }
  if (tree instanceof math.OperatorNode) {
    const args = tree.args;
    for (const arg of args) {
      if (isMathTreeRandom(arg, context)) {
        return true;
      }
    }
  }
  if (tree instanceof math.ParenthesisNode) {
    return isMathTreeRandom(tree.content, context);
  }
  if (tree instanceof math.SymbolNode) {
    const uid = tree.name;
    const { type, id } = separateUid(uid);
    if (type != "index") return false;
    if (id === context.affectedId && context.affectedType === "index") {
      return false;
    }
    const index = context.indices?.[id];
    if (index == null) {
      console.warn(`index ${id} not found`, context.indices);
      return false;
    }
    const mathNode = getExpressionMathNode(index.valueExpression, context);
    return isMathTreeRandom(mathNode, context);
  }
  return false;
}
